import { createRouter, createWebHistory } from 'vue-router';
import { authGuard } from '@auth0/auth0-vue';
import { createRedirectIfAuthenticatedGuard } from '@/utils/auth';
import { useFirstAccessibleRoute } from '@/composables/useFirstAccessibleRoute';
import MainLayout from '@/components/layouts/MainLayout.vue';
import AccountLayout from '@/components/layouts/AccountLayout.vue';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, _from, savedPosition) {
    if (to.meta.preserveScroll) return;
    const layoutContent = document.getElementById('layout-content');
    layoutContent?.scrollTo({ top: savedPosition?.top || 0 });
  },
  routes: [
    {
      path: '/agency-signup',
      name: 'agency-signup',
      component: () => import('@/views/AgencyOnboardingView.vue'),
    },
    {
      path: '/not-found',
      name: 'not-found',
      component: () => import('@/views/errors/NotFoundView.vue'),
    },
    {
      path: '/account-maintenance',
      name: 'account-maintenance',
      component: () => import('@/views/errors/AccountMaintenanceView.vue'),
    },
    {
      path: '/auth/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      beforeEnter: createRedirectIfAuthenticatedGuard(),
    },
    {
      path: '/auth/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/auth/ForgotPasswordView.vue'),
      beforeEnter: createRedirectIfAuthenticatedGuard(),
    },
    {
      path: '/auth/reset-password',
      name: 'reset-password',
      component: () => import('@/views/auth/ResetPasswordView.vue'),
      beforeEnter: createRedirectIfAuthenticatedGuard(),
    },
    {
      path: '/',
      name: 'root',
      component: MainLayout,
      beforeEnter: authGuard,
      children: [
        {
          path: 'search',
          name: 'search',
          meta: {
            sidebarExpanded: false,
            sidebarLocked: true,
            permission: ['statement:view', 'transfer:list', 'reservation:list', 'agency:list'],
          },
          component: () => import('@/views/search/SearchView.vue'),
          children: [
            {
              path: ':resourceType/:resourceId',
              name: 'search-preview',
              meta: { sidebarExpanded: false, sidebarLocked: true, preserveScroll: true },
              components: {
                preview: () => import('@/components/global-search/GlobalSearchPreview.vue'),
              },
            },
          ],
        },
        {
          path: 'statements',
          name: 'statements',
          meta: { permission: 'statement:view', menuOrder: 1 }, // TODO: Change to 'statement:list' when available
          component: () => import('@/views/statements/StatementsView.vue'),
        },
        {
          path: 'statements/:statementId',
          name: 'statement-details',
          redirect: (to) => ({
            name: 'statement-details-transfers',
            params: { statementId: to.params.statementId },
          }),
          meta: { sidebarExpanded: false, sidebarLocked: true, permission: 'statement:view' },
          components: {
            default: () => import('@/views/statements/StatementsDetailsView.vue'),
            summary: () => import('@/components/statements/StatementSummary.vue'),
          },
          children: [
            {
              path: 'transfers',
              name: 'statement-details-transfers',
              props: true,
              components: {
                transfers: () => import('@/views/statements/StatementsDetailsTransfersView.vue'),
              },
            },
            {
              path: 'transfers/:transferId',
              name: 'statement-details-transfer',
              meta: { preserveScroll: true },
              props: true,
              components: {
                transfers: () => import('@/views/statements/StatementsDetailsTransfersView.vue'),
              },
            },
            {
              path: 'reservations',
              name: 'statement-details-reservations',
              props: true,
              components: {
                reservations: () => import('@/views/statements/StatementsDetailsReservationsView.vue'),
              },
            },
            {
              path: 'reservations/:reservationId',
              name: 'statement-details-reservation',
              meta: { preserveScroll: true },
              props: true,
              components: {
                reservations: () => import('@/views/statements/StatementsDetailsReservationsView.vue'),
              },
            },
          ],
        },
        {
          path: 'statements/:statementId/reservations/create',
          name: 'create-reservation',
          meta: { sidebarExpanded: false, sidebarLocked: true, drawersHidden: true, permission: 'reservation:create' },
          component: () => import('@/views/reservations/CreateReservationView.vue'),
        },
        {
          path: 'statements/:statementId/reservations/:reservationId/edit',
          name: 'statement-details-reservation-edit',
          meta: { sidebarExpanded: false, sidebarLocked: true, drawersHidden: true, permission: 'reservation:edit' },
          component: () => import('@/views/reservations/EditReservationView.vue'),
        },
        {
          path: 'statements/create',
          name: 'statements-create',
          meta: { sidebarExpanded: false, sidebarLocked: true, permission: 'statement:create' },
          component: () => import('@/views/statements/CreateStatementView.vue'),
        },
        {
          path: 'transfers',
          name: 'transfers',
          meta: { sidebarExpanded: true, sidebarLocked: false, permission: 'transfer:list', menuOrder: 2 },
          component: () => import('@/views/transfers/TransfersView.vue'),
        },
        {
          path: 'transfers/:transferId',
          name: 'transfers-details',
          meta: { sidebarExpanded: false, sidebarLocked: true, permission: 'transfer:view' },
          components: {
            default: () => import('@/views/transfers/TransfersDetailsView.vue'),
            summary: () => import('@/components/transfers/TransferSummary.vue'),
          },
          children: [
            {
              path: 'reservations/:reservationId',
              name: 'transfers-details-reservation',
              meta: { preserveScroll: true, permission: 'reservation:view' },
              props: true,
              component: () => import('@/views/transfers/TransfersDetailsView.vue'),
            },
          ],
        },
        {
          path: 'agencies',
          name: 'agencies',
          meta: { permission: 'agency:view', menuOrder: 3 },
          component: () => import('@/views/agencies/AgenciesView.vue'),
          children: [
            {
              path: ':agencyId',
              name: 'agency',
              meta: { preserveScroll: true },
              props: true,
              components: {
                agencies: () => import('@/views/agencies/AgenciesView.vue'),
              },
            },
          ],
        },
        {
          path: 'agencies/:agencyId/details/beneficiary/create',
          name: 'agency-details-transfer-recipient-create',
          meta: { permission: 'transfer_recipient:create', drawersHidden: true, hideTermsFooter: true },
          component: () => import('@/views/agencies/TransferRecipientCreateUpdateView.vue'),
        },
        {
          path: 'agencies/:agencyId/details/beneficiary/:recipientId/edit',
          name: 'agency-details-transfer-recipient-edit',
          meta: {
            permission: 'transfer_recipient:edit',
            drawersHidden: true,
            hideTermsFooter: true,
          },
          component: () => import('@/views/agencies/TransferRecipientCreateUpdateView.vue'),
        },
        {
          path: 'agencies/:agencyId/details',
          name: 'agency-details',
          component: AccountLayout,
          meta: { permission: 'agency:setup' },
          redirect: (to) => {
            const agencyDetailsRoute = to.matched.find((route) => route.name === 'agency-details');
            const firstAccessibleRoute = useFirstAccessibleRoute(agencyDetailsRoute.children);
            return { name: firstAccessibleRoute.name };
          },
          children: [
            {
              path: 'beneficiary',
              name: 'agency-details-transfer-recipients',
              meta: { permission: 'agency:setup' },
              components: {
                default: () => import('@/views/agencies/TransferRecipientsView.vue'),
                navigation: () => import('@/components/agencies/AgencyDetailsNavigation.vue'),
              },
              children: [
                {
                  path: ':recipientId',
                  name: 'agency-details-transfer-recipient',
                  props: true,
                  meta: {
                    preserveScroll: true,
                    permission: 'agency:setup',
                  },
                  components: {
                    default: () => import('@/views/agencies/TransferRecipientsView.vue'),
                    navigation: () => import('@/components/agencies/AgencyDetailsNavigation.vue'),
                  },
                },
              ],
            },
            {
              path: 'identifiers',
              name: 'agency-details-identifiers',
              meta: { permission: 'agency:setup' },
              components: {
                default: () => import('@/views/agencies/AgencyIdentifiersView.vue'),
                navigation: () => import('@/components/agencies/AgencyDetailsNavigation.vue'),
              },
            },
            {
              path: 'users',
              name: 'agency-details-users',
              meta: { permission: 'agency:setup' },
              components: {
                default: () => import('@/views/agencies/AgencyUsersView.vue'),
                navigation: () => import('@/components/agencies/AgencyDetailsNavigation.vue'),
              },
            },
          ],
        },
        {
          path: 'suppliers',
          name: 'suppliers',
          meta: { permission: 'supplier:view', menuOrder: 4 },
          component: () => import('@/views/suppliers/SuppliersView.vue'),
        },
        {
          path: 'suppliers/:supplierId/details',
          name: 'supplier-details',
          component: AccountLayout,
          redirect: () => ({ name: 'funding-sources' }),
          children: [
            {
              path: 'funding-sources',
              name: 'funding-sources',
              meta: { permission: 'transfer_sender:view' },
              components: {
                default: () => import('@/views/account/FundingSourcesView.vue'),
                navigation: () => import('@/components/account/AccountNavigation.vue'),
              },
            },
            {
              path: 'invoices',
              name: 'invoices',
              meta: { permission: 'remittance_identity:manage' },
              components: {
                default: () => import('@/views/account/InvoicesView.vue'),
                navigation: () => import('@/components/account/AccountNavigation.vue'),
              },
            },
            {
              path: 'room-types',
              name: 'room-types',
              meta: { permission: 'room_type:view' }, // TODO: Change to 'room_type:list' when available
              components: {
                default: () => import('@/views/account/RoomTypesView.vue'),
                navigation: () => import('@/components/account/AccountNavigation.vue'),
              },
            },
            {
              path: 'rate-codes',
              name: 'rate-codes',
              meta: { permission: 'rate_code:view' }, // TODO: Change to 'reate_code:list' when available
              components: {
                default: () => import('@/views/account/RateCodesView.vue'),
                navigation: () => import('@/components/account/AccountNavigation.vue'),
              },
            },
            {
              path: 'users',
              name: 'users',
              meta: { permission: 'user:list:*' },
              components: {
                default: () => import('@/views/account/UsersView.vue'),
                navigation: () => import('@/components/account/AccountNavigation.vue'),
              },
            },
            {
              path: 'users/:userId',
              name: 'user-details',
              meta: { preserveScroll: true, permission: 'user:list:*' },
              components: {
                default: () => import('@/views/account/UsersView.vue'),
                navigation: () => import('@/components/account/AccountNavigation.vue'),
              },
            },
          ],
        },
        {
          path: '/suppliers/:supplierId/onboarding',
          name: 'onboarding',
          meta: { permission: 'transfer_sender:create' },
          component: () => import('@/views/account/onboarding/OnboardingView.vue'),
          redirect: () => ({ name: 'onboarding-kyc' }),
          children: [
            {
              path: 'kyc',
              name: 'onboarding-kyc',
              component: () => import('@/views/account/onboarding/OnboardingKycView.vue'),
            },
          ],
        },
        {
          name: 'simulations',
          path: '/simulations',
          component: () => import('@/views/simulation/SimulationView.vue'),
          meta: { permission: 'simulate', menuOrder: 4 },
        },
      ],
    },
    {
      path: '/agency-root',
      name: 'agency-root',
      component: MainLayout,
      beforeEnter: authGuard,
      redirect: () => ({ name: 'agency-home' }),
      children: [
        {
          path: '/home',
          name: 'agency-home',
          meta: { permission: 'transfer_recipient:view', menuOrder: 1 },
          component: () => import('@/views/agency-lite/AgencyHome.vue'),
        },
        {
          path: '/account-settings/beneficiary/create',
          name: 'agency-lite-transfer-recipient-create',
          meta: { permission: 'transfer_recipient:create', drawersHidden: true, hideTermsFooter: true },
          component: () => import('@/views/agency-lite/account/TransferRecipientCreateUpdateView.vue'),
        },
        {
          path: '/account-settings/beneficiary/:recipientId/edit',
          name: 'agency-lite-transfer-recipient-edit',
          meta: {
            permission: 'transfer_recipient:edit',
            drawersHidden: true,
            hideTermsFooter: true,
          },
          component: () => import('@/views/agency-lite/account/TransferRecipientCreateUpdateView.vue'),
        },
        {
          path: '/account-settings',
          name: 'agency-account',
          component: AccountLayout,
          redirect: (to) => {
            const agencyAccountRoute = to.matched.find((route) => route.name === 'agency-account');
            const firstAccessibleRoute = useFirstAccessibleRoute(agencyAccountRoute.children);
            return { name: firstAccessibleRoute.name };
          },
          children: [
            {
              path: 'beneficiary',
              name: 'agency-lite-transfer-recipients',
              meta: { permission: 'transfer_recipient:view' },
              components: {
                default: () => import('@/views/agency-lite/account/TransferRecipientsView.vue'),
                navigation: () => import('@/components/account/AgencyAccountNavigation.vue'),
              },
              children: [
                {
                  path: ':recipientId',
                  name: 'agency-lite-transfer-recipient',
                  props: true,
                  meta: {
                    preserveScroll: true,
                    permission: 'transfer_recipient:view',
                  },
                  components: {
                    default: () => import('@/views/agency-lite/account/TransferRecipientsView.vue'),
                    navigation: () => import('@/components/account/AgencyAccountNavigation.vue'),
                  },
                },
              ],
            },
            {
              path: 'identifiers',
              name: 'agency-lite-identifiers',
              meta: { permission: 'transfer_recipient:view' }, // TODO: Change to 'identifiers:view' when available??
              components: {
                default: () => import('@/views/agency-lite/account/AgencyIdentifiersView.vue'),
                navigation: () => import('@/components/account/AgencyAccountNavigation.vue'),
              },
            },
          ],
        },
        {
          path: '/agency-simulations',
          name: 'agency-simulations',
          component: () => import('@/views/simulation/AgencySimulationView.vue'),
          meta: { permission: 'simulate', menuOrder: 999 },
        },
      ],
    },
    {
      name: 'icons',
      path: '/icons',
      component: () => import('@/views/icons/IconsShowcaseView.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'catch-all',
      component: () => import('@/views/errors/NotFoundView.vue'),
    },
  ],
});

export default router;
